import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, fileUrl, downloadFile } from "@/lib/api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input, Textarea, Select, StatusBadge, Attachment } from "@/components/shared";
import { ArrowLeft, Pencil, Save, X, Trash2, Archive, ArchiveRestore, Clock, MapPin, Sparkles, LogIn, LogOut, Receipt, FileText, Download, DollarSign, Check, Navigation, ListChecks, ChevronDown, Camera, Plus, Send } from "lucide-react";
import { Lightbox } from "@/components/Lightbox";
import { DocumentManager } from "@/components/DocumentManager";
import { JobChat } from "@/components/JobChat";
import { PhotoGallery } from "@/components/PhotoGallery";
import { OfficeNotes } from "@/components/OfficeNotes";

const SERVICE_TYPES = ["Property Preservation", "Initial Services", "Inspection", "Occupancy Check", "Eviction", "REO Services", "Maid Refresh / Janitorial", "Lock Changes / Re-Key", "Debris Removal", "Winterization", "De-Winterization", "Grass Cut / Lawn Maintenance", "Securing / Board-Up", "Pool Securing", "Repairs", "Virtual Tour", "Mobile Notary", "Real Estate Photography", "Construction"];
const STATUSES = ["active", "in_progress", "completed"];

function Section({ title, icon: Icon, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const [empty, setEmpty] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setEmpty(el.children.length === 0);
    check();
    const mo = new MutationObserver(check);
    mo.observe(el, { childList: true });
    return () => mo.disconnect();
  }, []);
  return (
    <Card className={`overflow-hidden ${empty ? "hidden" : ""}`} data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-900/40 transition-colors">
        <span className="flex items-center gap-2 font-head font-bold uppercase text-white text-sm tracking-wide">{Icon && <Icon size={16} className="text-[#FF5722]" />} {title}</span>
        <ChevronDown size={18} className={`text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div ref={ref} className={`px-4 pb-4 pt-4 border-t border-zinc-800 space-y-4 [&>*]:!border-0 [&>*]:!bg-transparent [&>*]:!shadow-none [&>*]:!p-0 ${open ? "" : "hidden"}`} data-testid={`${testid}-body`}>{children}</div>
    </Card>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [crew, setCrew] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [clients, setClients] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [payPreview, setPayPreview] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("gallery");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      setForm(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/jobs");
    }
  }, [id, navigate]);

  const loadPhotos = useCallback(() => api.get(`/jobs/${id}/photos`).then((r) => setPhotos(r.data)).catch(() => {}), [id]);

  const shareLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't supported on this device"); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/jobs/${id}/share-location`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success(data.eta_min != null ? `En route — ~${data.distance_mi} mi away, ETA ~${data.eta_min} min` : "Location shared with dispatch");
        load();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }, () => toast.error("Couldn't get your location — enable location access"));
  };
  const loadHistory = useCallback(() => api.get(`/jobs/${id}/history`).then((r) => setHistory(r.data)).catch(() => {}), [id]);

  useEffect(() => { load(); loadPhotos(); loadHistory(); }, [load, loadPhotos, loadHistory]);
  useEffect(() => { if (user?.role === "admin") { api.get("/crew").then((r) => setCrew(r.data)); api.get("/client-accounts").then((r) => setClients(r.data)); api.get("/pricing/sheets").then((r) => setSheets(r.data)).catch(() => {}); api.get("/checklist-templates").then((r) => setTemplates(r.data)).catch(() => {}); } }, [user]);
  useEffect(() => { if (user?.role === "admin" && id) { api.get(`/jobs/${id}/assignable-crew`).then((r) => setAssignable(r.data.contractors)).catch(() => {}); } }, [user, id, job?.address]);

  const isAdmin = user?.role === "admin";
  const isCrew = user?.role === "crew";
  const isClient = user?.role === "client";

  const applyPricing = async (sheetId, service) => {
    if (!sheetId) { setPayPreview(null); return; }
    try {
      const { data } = await api.get("/pricing/match", { params: { sheet_id: sheetId, service: service || "" } });
      if (data.matched) { setForm((f) => ({ ...f, allowable_payout: data.matched.client_price ?? f.allowable_payout })); setPayPreview(data.matched); }
      else setPayPreview({ none: true });
    } catch { setPayPreview(null); }
  };

  const addLine = () => setForm((f) => ({ ...f, scope_lines: [...(f.scope_lines || []), { service: "", qty: 1 }] }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, scope_lines: (f.scope_lines || []).map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  const rmLine = (i) => setForm((f) => ({ ...f, scope_lines: (f.scope_lines || []).filter((_, idx) => idx !== i) }));
  const attachChecklist = async (tid) => { if (!tid) return; try { const { data } = await api.post(`/jobs/${job.id}/attach-checklist`, { template_id: tid }); setJob(data); toast.success("Checklist attached"); } catch (e) { toast.error(e.response?.data?.detail || "Could not attach"); } };
  const clearChecklist = async () => { try { const { data } = await api.post(`/jobs/${job.id}/attach-checklist`, { clear: true }); setJob(data); toast.success("Checklist cleared"); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  const save = async () => {
    try {
      const payload = isAdmin
        ? { address: form.address, client_name: form.client_name, client_id: form.client_id || null, source_client_id: form.source_client_id || null, source_client_name: form.source_client_name || "", service_type: form.service_type, status: form.status, scope: form.scope, scheduled_date: form.scheduled_date || null, assigned_crew_id: form.assigned_crew_id || null, allowable_payout: (form.allowable_payout === "" || form.allowable_payout == null) ? undefined : Number(form.allowable_payout), scope_lines: form.scope_lines || [],
            special_instructions: form.special_instructions ?? "", work_order: form.work_order ?? "", lock_code: form.lock_code ?? "", due_date: form.due_date || undefined, price_sheet_id: form.price_sheet_id || null }
        : { status: form.status, ...(form.price_sheet_id !== undefined ? { price_sheet_id: form.price_sheet_id || null } : {}) };
      const { data } = await api.put(`/jobs/${id}`, payload);
      setJob(data); setForm(data); setEditing(false);
      toast.success("Job updated");
      loadHistory();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const quickStatus = async (status) => {
    try {
      const { data } = await api.put(`/jobs/${id}`, { status });
      setJob(data); setForm(data); toast.success(`Status → ${status.replace("_", " ")}`); loadHistory();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doArchive = async (val) => {
    await api.post(`/jobs/${id}/archive`, null, { params: { archived: val } });
    toast.success(val ? "Archived" : "Restored"); load();
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this job permanently?")) return;
    await api.delete(`/jobs/${id}`); toast.success("Job deleted"); navigate("/jobs");
  };

  const uploadFile = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { id: data.id };
  };

  const aiDraftScope = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/draft-scope", { description: form.scope || `${form.service_type} at ${form.address}`, service_type: form.service_type, address: form.address });
      setForm((f) => ({ ...f, scope: data.scope }));
      toast.success("AI drafted the scope");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };
  const checkIn = async () => {
    try { await api.post(`/jobs/${id}/checkin`); toast.success("Checked in"); load(); loadHistory(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const checkOut = async () => {
    try { await api.post(`/jobs/${id}/checkout`); toast.success("Checked out"); load(); loadHistory(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleInvoice = async () => {
    try { await api.post(`/jobs/${id}/invoice-ready`, null, { params: { ready: !job.ready_for_invoicing } }); toast.success(job.ready_for_invoicing ? "Invoicing flag cleared" : "Marked ready for invoicing"); load(); loadHistory(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const createInvoice = async () => {
    try {
      const { data } = await api.post(`/jobs/${id}/create-invoice`);
      toast.success("Draft invoice created from this job");
      navigate(`/bids?open=${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!job) return <div className="font-mono text-zinc-500 animate-pulse">Loading…</div>;

  const myOpenCheckin = (job.check_ins || []).some((c) => c.out === null && c.user_id === user.id);
  const tabs = [["gallery", `Gallery (${photos.length})`], ["documents", "Documents"], ["chat", "Chat"]];
  if (isAdmin) tabs.push(["office", "Office"]);
  if (isAdmin) tabs.push(["history", "History"]);

  return (
    <div className="space-y-6">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-[#FF5722] font-mono text-sm uppercase tracking-wide"><ArrowLeft size={16} /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4 animate-rise">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722] bg-[#FF5722]/10 border border-[#FF5722]/30 px-2 py-0.5">{job.service_type}</span>
            <StatusBadge status={job.archived ? "archived" : job.status} />
            {job.ready_for_invoicing && <span data-testid="invoice-badge" className="font-mono text-[9px] uppercase tracking-wider bg-emerald-500 text-black border border-black px-1.5 py-0.5">Invoice Ready</span>}
          </div>
          <h1 className="font-head font-black uppercase text-2xl sm:text-3xl text-white tracking-tight flex items-start gap-2"><MapPin className="text-zinc-600 mt-1 shrink-0" /> {job.address}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && !editing && <Btn variant="secondary" data-testid="edit-job-btn" onClick={() => setEditing(true)}><Pencil size={14} /> Edit</Btn>}
          {isAdmin && (job.archived
            ? <Btn variant="secondary" data-testid="unarchive-btn" onClick={() => doArchive(false)}><ArchiveRestore size={14} /> Restore</Btn>
            : <Btn variant="secondary" data-testid="archive-btn" onClick={() => doArchive(true)}><Archive size={14} /> Archive</Btn>)}
          {isAdmin && <Btn variant="danger" data-testid="delete-job-btn" onClick={doDelete}><Trash2 size={14} /></Btn>}
          {isAdmin && !job.archived && <Btn variant="secondary" data-testid="invoice-ready-btn" onClick={toggleInvoice}><Receipt size={14} /> {job.ready_for_invoicing ? "Invoicing ✓" : "Ready to Invoice"}</Btn>}
          {isAdmin && job.ready_for_invoicing && !job.archived && <Btn data-testid="create-invoice-btn" onClick={createInvoice}><FileText size={14} /> Create Invoice</Btn>}
        </div>
      </div>

      {/* Check-in / Check-out */}
      {(isCrew || isAdmin) && !job.archived && (
        <Section title="On-Site Time & Check-In" icon={LogIn} testid="section-checkin">
        <Card className="p-4" data-testid="checkin-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Label>On-Site Time Tracking</Label>
              <div className="font-mono text-xs text-zinc-500">{(job.check_ins || []).length} check-in(s){myOpenCheckin ? " · you're on site now" : ""}</div>
            </div>
            {myOpenCheckin
              ? <Btn variant="danger" data-testid="checkout-btn" onClick={checkOut}><LogOut size={14} /> Check Out</Btn>
              : <Btn data-testid="checkin-btn" onClick={checkIn}><LogIn size={14} /> Check In</Btn>}
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-zinc-500 text-xs flex items-start gap-1.5 flex-1 min-w-[200px]"><Navigation size={13} className="text-[#FF5722] mt-0.5 shrink-0" /> When you head to the property, tap <span className="text-white font-semibold">&nbsp;I'm en route&nbsp;</span> to send dispatch your live location &amp; ETA, then <span className="text-white font-semibold">&nbsp;Check In&nbsp;</span> on arrival.</p>
            <Btn variant="secondary" data-testid="share-location-btn" onClick={shareLocation}><Navigation size={13} /> I'm en route — share location</Btn>
          </div>
          {(job.check_ins || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-800 space-y-1 max-h-40 overflow-y-auto">
              {job.check_ins.slice().reverse().map((c) => (
                <div key={c.id} className="flex items-center justify-between font-mono text-[11px] text-zinc-500">
                  <span className="text-zinc-300">{c.user_name}</span>
                  <span>{new Date(c.in).toLocaleString()} → {c.out ? new Date(c.out).toLocaleTimeString() : <span className="text-[#FF5722]">on site</span>}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        </Section>
      )}

      <Section title="Work Order Details" icon={FileText} testid="section-details">
        <Card className="p-6">
          {editing && isAdmin ? (
            <div className="space-y-4">
              <div><Label>Address</Label><Input data-testid="edit-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Client</Label><Input data-testid="edit-client" value={form.client_name || ""} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
                <div><Label>Client Account</Label><Select data-testid="edit-client-account" value={form.source_client_id || (form.client_id ? `u:${form.client_id}` : "")} onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); setForm({ ...form, source_client_id: c?.source_client_id || null, source_client_name: c ? (c.company || c.name) : "", client_name: c ? (c.company || c.name) : form.client_name, client_id: c?.user_id || null }); }}><option value="">— None —</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.company} (${c.name})` : c.name}</option>)}</Select></div>
                <div><Label>Service Type</Label><Select data-testid="edit-service" value={form.service_type} onChange={(e) => { setForm({ ...form, service_type: e.target.value }); applyPricing(form.price_sheet_id, e.target.value); }}>{SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}</Select></div>
                <div><Label>Client Price Sheet (sets allowable)</Label><Select data-testid="edit-price-sheet" value={form.price_sheet_id || ""} onChange={(e) => { setForm({ ...form, price_sheet_id: e.target.value }); applyPricing(e.target.value, form.service_type); }}><option value="">— None —</option>{sheets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></div>
                <div><Label>Status</Label><Select data-testid="edit-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</Select></div>
                <div><Label>Scheduled Date</Label><Input data-testid="edit-date" type="date" value={(form.scheduled_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Assign Crew (closest first)</Label><Select data-testid="edit-crew" value={form.assigned_crew_id || ""} onChange={(e) => setForm({ ...form, assigned_crew_id: e.target.value })}><option value="">Unassigned</option>{(assignable.length ? assignable : crew.map((c) => ({ ...c, distance_mi: null }))).map((c) => <option key={c.id} value={c.id}>{c.name}{c.distance_mi != null ? ` — ${c.distance_mi} mi${c.in_range ? "" : " (out of range)"}` : " — distance n/a"}{c.offers_service ? " ✓" : ""}</option>)}</Select><div className="font-mono text-[10px] text-zinc-600 mt-1">Sorted closest to farthest. Contractors beyond 100 mi are marked "out of range" but can still be assigned.</div></div>
                <div className="sm:col-span-2"><Label>Allowable contractor payout (margin cap, USD)</Label><Input data-testid="edit-allowable" type="number" step="0.01" value={form.allowable_payout ?? ""} onChange={(e) => setForm({ ...form, allowable_payout: e.target.value })} placeholder="Max you'll pay the contractor for this job" />
                  {payPreview && !payPreview.none && <p className="mt-1 font-mono text-[11px] text-emerald-400" data-testid="pay-preview">Auto-filled from sheet · {payPreview.service}: client ${Number(payPreview.client_price ?? 0).toFixed(2)} → contractor pays ${Number(payPreview.contractor_pay ?? 0).toFixed(2)}</p>}
                  {payPreview?.none && <p className="mt-1 font-mono text-[11px] text-amber-400" data-testid="pay-preview">No sheet line matches "{form.service_type}". Set the price in the Pricing page or adjust the service type.</p>}
                </div>
                <div className="sm:col-span-2 border-t border-zinc-800 pt-3 space-y-3">
                  <div>
                    <Label>Attach a checklist (auto-attaches by scope when created)</Label>
                    <Select data-testid="attach-checklist-select" value="" onChange={(e) => { attachChecklist(e.target.value); e.target.value = ""; }}><option value="">— Add checklist… —</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</Select>
                    {(job.checklist_items || []).length > 0 && <p className="mt-1 font-mono text-[11px] text-zinc-500">{job.checklist_items.length} checklist item(s) linked. <button type="button" data-testid="clear-checklist-btn" onClick={clearChecklist} className="text-[#FF5722] hover:underline">Clear</button></p>}
                  </div>
                  <div>
                    <Label>Pay lines (per service — builds the contractor pay draft)</Label>
                    <div className="space-y-2">
                      {(form.scope_lines || []).map((l, i) => (
                        <div key={i} className="flex gap-2">
                          <Input data-testid={`line-service-${i}`} value={l.service} onChange={(e) => setLine(i, { service: e.target.value })} placeholder="Service (matches price sheet)" className="flex-1" />
                          <Input data-testid={`line-qty-${i}`} type="number" step="0.01" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="w-20" />
                          <button type="button" data-testid={`rm-line-${i}`} onClick={() => rmLine(i)} className="text-zinc-500 hover:text-red-400 shrink-0 px-2 font-bold">✕</button>
                        </div>
                      ))}
                    </div>
                    <Btn variant="ghost" data-testid="add-line-btn" onClick={addLine} className="mt-2"><Plus size={14} /> Add pay line</Btn>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Scope / Notes</Label>
                  <button type="button" data-testid="ai-scope-btn" onClick={aiDraftScope} disabled={aiBusy} className="flex items-center gap-1 text-[#FF5722] hover:text-[#F4511E] font-mono text-[10px] uppercase tracking-widest disabled:opacity-50 mb-1.5"><Sparkles size={12} /> {aiBusy ? "Drafting…" : "AI Draft"}</button>
                </div>
                <Textarea data-testid="edit-scope" value={form.scope || ""} onChange={(e) => setForm({ ...form, scope: e.target.value })} />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label>Work Order #</Label><Input data-testid="edit-work-order" value={form.work_order || ""} onChange={(e) => setForm({ ...form, work_order: e.target.value })} /></div>
                <div><Label>Client Due Date</Label><Input data-testid="edit-due" type="date" value={(form.due_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                <div><Label>Lock / Key Codes</Label><Input data-testid="edit-lock" value={form.lock_code || ""} onChange={(e) => setForm({ ...form, lock_code: e.target.value })} placeholder="LB:1234 KEY:5678" /></div>
              </div>
              <div><Label>Special Instructions (shown to the contractor in the checklist)</Label><Textarea data-testid="edit-special" value={form.special_instructions || ""} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })} placeholder="Client photo specs, deadlines, permit rules, who to call from site…" /></div>
              <div className="flex gap-2">
                <Btn data-testid="save-job-btn" onClick={save}><Save size={14} /> Save</Btn>
                <Btn variant="secondary" onClick={() => { setEditing(false); setForm(job); }}><X size={14} /> Cancel</Btn>
              </div>
            </div>
          ) : (
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {!isCrew && <Detail label="Client" value={job.client_name || "—"} />}
              <Detail label="Service Type" value={job.service_type} />
              {isAdmin && <Detail label="Client Account (admin only)" value={job.client_account ? <span data-testid="client-account">{job.client_account.name}{job.client_account.company ? ` · ${job.client_account.company}` : ""}<span className="block font-mono text-xs text-zinc-500">{job.client_account.email}{job.client_account.phone ? ` · ${job.client_account.phone}` : ""}</span></span> : <span className="text-zinc-500">Not linked — edit to match a client account</span>} />}
              {job.work_order && <Detail label="Work Order #" value={job.work_order} />}
              <Detail label="Assigned Crew" value={job.assigned_crew_name || "Unassigned"} />
              <Detail label="Scheduled" value={job.scheduled_date ? new Date(job.scheduled_date).toLocaleDateString() : "—"} />
              {job.due_date && <Detail label="Client Due Date" value={new Date(job.due_date).toLocaleDateString()} />}
              {!isClient && job.lock_code && <Detail label="Lock / Key Codes" value={<span className="font-mono">{job.lock_code}</span>} />}
              {isCrew && <Detail label="Your Payout" value={job.contractor_payout != null ? <span className="text-emerald-400 font-mono" data-testid="crew-payout">{`$${Number(job.contractor_payout).toFixed(2)} agreed`}</span> : job.contractor_pay != null ? <span className="text-emerald-400 font-mono" data-testid="crew-payout">{`$${Number(job.contractor_pay).toFixed(2)}`}</span> : job.allowable_payout != null ? <span className="font-mono" data-testid="crew-payout">{`Up to $${Number(job.allowable_payout).toFixed(2)} — bid below`}</span> : <span className="text-zinc-500" data-testid="crew-payout">Submit your bid below</span>} />}
              <div className="sm:col-span-2"><Detail label="Scope / Notes" value={<span className="whitespace-pre-wrap">{job.scope || "—"}</span>} /></div>
              {job.special_instructions && <div className="sm:col-span-2"><Detail label="Special Instructions" value={<span className="whitespace-pre-wrap text-[#FF5722]">{job.special_instructions}</span>} /></div>}
            </dl>
          )}
        </Card>
      </Section>


      {isAdmin && !job.archived && (
        <Section title="Assign & Find Contractors" icon={Navigation} testid="section-assign">
          <MatchPanel jobId={job.id} />
          <NotifyNearby job={job} />
        </Section>
      )}

      {isAdmin && job.last_crew_location && <JobMiniMap job={job} />}

      {!job.archived && (isAdmin || isCrew) && (
        <Section title="Contractor Pay" icon={DollarSign} testid="section-paydraft">
          <JobPayDraft job={job} isAdmin={isAdmin} />
        </Section>
      )}

      {!job.archived && (isAdmin || isCrew) && (
        <Section title="Payout & Bids" icon={Receipt} testid="section-payout">
          <PayoutPanel job={job} setJob={setJob} isAdmin={isAdmin} loadHistory={loadHistory} />
        </Section>
      )}

      {!job.archived && (isAdmin || isCrew) && (
        <Section title="Pricing" icon={DollarSign} testid="section-pricing">
          <JobPricingPanel job={job} isAdmin={isAdmin} />
        </Section>
      )}

      {isAdmin && !job.archived && (
        <Section title="Client Payments" icon={Receipt} testid="section-client-payments">
          <ClientPaymentsPanel job={job} setJob={setJob} />
        </Section>
      )}

      {/* Crew quick status */}
      {isCrew && !job.archived && (
        <Section title="Update Status" testid="section-status">
        <Card className="p-4">
          <Label>Update Status</Label>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`crew-status-${s}`} onClick={() => quickStatus(s)}
                className={`font-mono text-xs uppercase px-3 py-2 border-2 transition-all ${job.status === s ? "bg-[#FF5722] text-black border-black" : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-[#FF5722]"}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </Card>
        </Section>
      )}

      {(isCrew || isAdmin) && !job.archived && (
        <Section title="Photos, Checklist & Instructions" icon={Camera} testid="section-photos">
          <PhotoChecklist job={job} canEdit onChange={(cl) => setJob((j) => ({ ...j, checklist: cl }))} onPhotoAdded={loadPhotos} />
        </Section>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b-2 border-zinc-800 overflow-x-auto">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`font-head font-bold uppercase text-sm tracking-wide px-4 py-2.5 border-b-4 -mb-0.5 transition-all ${tab === k ? "border-[#FF5722] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{l}</button>
        ))}
      </div>

      {tab === "office" && isAdmin && <OfficeNotes job={job} setJob={setJob} />}

      {tab === "gallery" && (
        <PhotoGallery jobId={id} photos={photos} reload={loadPhotos} canUpload={!job.archived} isAdmin={isAdmin} isClient={isClient} uploadFile={uploadFile} />
      )}

      {tab === "documents" && (
        <Card className="p-5">
          <DocumentManager basePath={`/jobs/${id}/documents`} title="Work Order Documents" testid="job-docs"
            hint={isAdmin ? "Client work orders, winterization stickers, receipts, permits and anything else tied to this job. Visible to the assigned contractor." : "Upload winterization stickers, dump receipts, signed forms and any paperwork for this work order."}
            canDelete />
        </Card>
      )}

      {tab === "chat" && <JobChat job={job} user={user} />}

      {tab === "history" && isAdmin && (
        <Card className="p-6">
          {history.length === 0 ? <div className="text-zinc-600 font-mono">No history.</div> : (
            <ol className="space-y-4">
              {history.map((h) => (
                <li key={h.id} className="flex gap-3" data-testid={`history-${h.id}`}>
                  <div className="mt-1"><Clock size={16} className="text-[#FF5722]" /></div>
                  <div className="pb-4 border-b border-zinc-800 flex-1">
                    <div className="text-white font-semibold text-sm">{h.action} {h.detail && <span className="text-zinc-400 font-normal">— {h.detail}</span>}</div>
                    <div className="font-mono text-[11px] text-zinc-600 mt-0.5">{h.by} · {new Date(h.at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      )}
    </div>
  );
}

const PP_CHECKLIST = [
  ["Arrival & exterior", ["Street view showing the house number", "Front, rear and both side elevations", "Roof and any visible exterior damage", "Front door and any posted notices"]],
  ["Every task — before & after", ["Clear BEFORE and AFTER photo of each task", "Grass cut: before + after (show a measurement for overgrowth)", "Trash-out / debris: before, mid-load, after + itemized count", "Winterization: each fixture + a photo of the winterization label", "Lock change: new lock installed + lockbox and posted code", "Boarding: each opening before + after"]],
  ["Interior", ["Each room before / during / after", "Close-up of any damage, mold or standing water with a note", "Utility meters (gas, water, electric) with readings"]],
  ["Photo quality & procedure", ["Turn the timestamp ON for every photo", "Shoot in good light and keep it in focus", "Include a reference object for scale on damages", "Keep fingers off the lens — no blurry shots"]],
];

function PhotoChecklist({ job, canEdit, onChange, onPhotoAdded }) {
  const [open, setOpen] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const state = job.checklist || {};
  const CHECK_GROUPS = job.checklist_items?.length ? [["Required Checklist", job.checklist_items.map((i) => i.label)]] : PP_CHECKLIST;
  const keyOf = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 80);
  const all = CHECK_GROUPS.flatMap(([, items]) => items);
  const done = all.filter((it) => state[keyOf(it)]).length;
  const withPhoto = all.filter((it) => state[keyOf(it)]?.photo_id).length;
  const post = async (it, body) => {
    const { data } = await api.post(`/jobs/${job.id}/checklist`, { key: keyOf(it), label: it, ...body });
    onChange?.(data.checklist);
  };
  const toggle = async (it) => {
    if (!canEdit) return;
    try { await post(it, { done: !state[keyOf(it)] }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const attach = async (it, e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusyKey(keyOf(it));
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd);
      await post(it, { done: true, photo_id: up.data.id });
      onPhotoAdded?.();
      toast.success("Photo attached to checklist item");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };
  return (
    <Card className="p-4" data-testid="photo-checklist">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2">
        <span className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5 text-left"><ListChecks size={15} className="text-[#FF5722] shrink-0" /> Required Photos & Procedures Checklist</span>
        <span className="flex items-center gap-2 shrink-0">
          <span data-testid="checklist-progress" className={`font-mono text-[11px] px-2 py-0.5 border ${done === all.length ? "bg-emerald-500 text-black border-black" : "text-[#FF5722] border-[#FF5722]/50"}`}>{done}/{all.length}</span>
          <span data-testid="checklist-photos" className="font-mono text-[11px] px-2 py-0.5 border border-zinc-700 text-zinc-400 flex items-center gap-1"><Camera size={11} /> {withPhoto}</span>
          <ChevronDown size={16} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          {job.special_instructions && (
            <div className="border-l-4 border-[#FF5722] bg-[#FF5722]/5 p-3" data-testid="special-instructions">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-1">Client special instructions — read first</div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{job.special_instructions}</p>
            </div>
          )}
          {CHECK_GROUPS.map(([sec, items]) => (
            <div key={sec}>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-1.5">{sec}</div>
              <ul className="space-y-1.5">
                {items.map((it) => {
                  const k = keyOf(it);
                  const st = state[k];
                  return (
                    <li key={it} className="flex items-start gap-2">
                      <label className={`flex items-start gap-2 text-sm flex-1 min-w-0 ${canEdit ? "cursor-pointer" : ""} ${st ? "text-zinc-500 line-through" : "text-zinc-200"}`}>
                        <input type="checkbox" data-testid={`check-${k}`} checked={!!st} onChange={() => toggle(it)} disabled={!canEdit} className="mt-0.5 w-4 h-4 accent-[#FF5722] shrink-0" />
                        <span>{it}{st && <span className="block font-mono text-[10px] text-zinc-600 no-underline">✓ {st.by} · {new Date(st.at).toLocaleString()}</span>}</span>
                      </label>
                      <div className="flex items-center gap-1 shrink-0">
                        {st?.photo_id && (
                          <button type="button" data-testid={`check-photo-${k}`} onClick={() => setPreview({ src: fileUrl(st.photo_id), name: it })} title="View attached photo" className="w-9 h-9 border-2 border-emerald-600 overflow-hidden bg-zinc-950">
                            <img src={fileUrl(st.photo_id)} alt="" className="w-full h-full object-cover" />
                          </button>
                        )}
                        {canEdit && (
                          <label className={`w-9 h-9 border-2 flex items-center justify-center cursor-pointer ${st?.photo_id ? "border-zinc-800 text-zinc-500" : "border-zinc-700 text-[#FF5722] hover:border-[#FF5722]"} ${busyKey === k ? "opacity-50 pointer-events-none" : ""}`} title={st?.photo_id ? "Replace photo" : "Attach the photo for this item"}>
                            <Camera size={14} />
                            <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={`check-attach-${k}`} onChange={(e) => attach(it, e)} />
                          </label>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <p className="text-zinc-500 text-xs border-t border-zinc-800 pt-3">Tap the camera on each item to attach the exact photo — dispatch can verify every required shot in one click. Photos also land in the Gallery.</p>
        </div>
      )}
      <Lightbox items={preview ? [preview] : []} index={preview ? 0 : null} setIndex={() => {}} onClose={() => setPreview(null)} />
    </Card>
  );
}

function NotifyNearby({ job }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get(`/jobs/${job.id}/nearby-contractors`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job.id]);
  const notify = async (c) => {
    setBusy(c.id);
    try { const { data } = await api.post(`/jobs/${job.id}/notify-contractor`, { crew_id: c.id }); toast.success(`${c.name} notified${data.emailed ? " + emailed" : ""}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not notify"); }
    finally { setBusy(null); }
  };
  if (!list) return null;
  const near = list.filter((c) => c.within_radius);
  const show = near.length ? near : list.slice(0, 8);
  return (
    <Card className="p-4 space-y-3" data-testid="notify-nearby-panel">
      <div className="flex items-center gap-2"><MapPin size={16} className="text-[#FF5722]" /><span className="font-head font-bold uppercase text-white text-sm tracking-wide">Notify Nearby Contractors</span><span className="font-mono text-[10px] text-zinc-500">{near.length} in range</span></div>
      <div className="divide-y divide-zinc-800 border border-zinc-800 max-h-72 overflow-y-auto">
        {show.map((c) => (
          <div key={c.id} data-testid={`nearby-${c.id}`} className="flex items-center gap-3 px-3 py-2 text-sm">
            <div className="flex-1 min-w-0">
              <div className="text-white truncate">{c.name} {c.state && <span className="font-mono text-[9px] text-zinc-500">· {c.state}</span>}</div>
              <div className="font-mono text-[10px] text-zinc-600">{c.distance_mi != null ? `${c.distance_mi} mi` : "distance n/a"}{c.available ? " · available" : " · unavailable"}{c.within_radius ? " · in range" : ""}</div>
            </div>
            <Btn variant="secondary" data-testid={`notify-${c.id}`} disabled={busy === c.id} onClick={() => notify(c)}><Send size={13} /> {busy === c.id ? "…" : "Notify"}</Btn>
          </div>
        ))}
        {show.length === 0 && <div className="px-3 py-4 text-center text-zinc-600 font-mono text-xs">No contractors found.</div>}
      </div>
      <p className="font-mono text-[10px] text-zinc-600">Notify opens a chat conversation and emails the contractor the job + description.</p>
    </Card>
  );
}

function ClientPaymentsPanel({ job, setJob }) {
  const [form, setForm] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), method: "Check", note: "" });
  const [busy, setBusy] = useState(false);
  const pays = job.client_payments || [];
  const m = (v) => { const n = Number(v || 0); return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
  const total = pays.reduce((s, p) => s + Number(p.amount || 0), 0);
  const contractorPay = job.contractor_payout != null ? Number(job.contractor_payout) : (job.contractor_pay != null ? Number(job.contractor_pay) : 0);
  const profit = total - contractorPay;
  const add = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/client-payments`, { amount: Number(form.amount), date: form.date, method: form.method, note: form.note });
      setJob(r.data);
      setForm({ amount: "", date: new Date().toISOString().slice(0, 10), method: "Check", note: "" });
      toast.success("Client payment recorded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (pid) => {
    try { const r = await api.delete(`/jobs/${job.id}/client-payments/${pid}`); setJob(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-4 space-y-3" data-testid="client-payments-panel">
      <div className="flex items-center gap-2"><Receipt size={16} className="text-[#FF5722]" /><span className="font-head font-bold uppercase text-white text-sm tracking-wide">Client Payments (admin only)</span></div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-sm">
        <span className="text-zinc-400">Client paid: <span className="text-white" data-testid="client-paid-total">{m(total)}</span></span>
        <span className="text-zinc-400">Contractor pay: <span className="text-emerald-400">{m(contractorPay)}</span></span>
        <span className="text-zinc-400">Profit: <span className={profit >= 0 ? "text-emerald-400" : "text-red-400"} data-testid="client-profit">{m(profit)}</span></span>
      </div>
      {pays.length > 0 && (
        <div className="divide-y divide-zinc-800 border border-zinc-800">
          {pays.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`client-payment-${p.id}`}>
              <div className="flex-1 min-w-0"><div className="text-white">{m(p.amount)} <span className="font-mono text-[10px] text-zinc-500">· {p.method}</span></div><div className="font-mono text-[10px] text-zinc-600">{(p.date || "").slice(0, 10)}{p.note ? ` — ${p.note}` : ""}</div></div>
              <button onClick={() => del(p.id)} data-testid={`del-client-payment-${p.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <div><Label>Amount</Label><Input type="number" step="0.01" data-testid="client-payment-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" /></div>
        <div><Label>Date paid</Label><Input type="date" data-testid="client-payment-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
        <div><Label>Method</Label><Select data-testid="client-payment-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>{["Check", "ACH / Transfer", "Card", "Cash", "Other"].map((x) => <option key={x} value={x}>{x}</option>)}</Select></div>
        <div><Label>Note</Label><Input data-testid="client-payment-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Invoice #, ref…" /></div>
      </div>
      <Btn onClick={add} disabled={busy} data-testid="add-client-payment-btn"><Plus size={14} /> Record client payment</Btn>
    </Card>
  );
}

function JobPayDraft({ job, isAdmin }) {
  const [d, setD] = useState(null);
  const slKey = JSON.stringify(job.scope_lines || []);
  useEffect(() => { api.get(`/jobs/${job.id}/pay-draft`).then((r) => setD(r.data)).catch(() => setD(null)); }, [job.id, job.price_sheet_id, job.service_type, slKey]);
  if (!d || !d.lines?.length) return null;
  const m = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
  return (
    <Card className="p-4 space-y-3" data-testid="pay-draft-panel">
      <div className="flex items-center gap-2"><DollarSign size={16} className="text-[#FF5722]" /><span className="font-head font-bold uppercase text-white text-sm tracking-wide">{isAdmin ? "Payout Breakdown (draft)" : "Your estimated pay for this job"}</span>{d.sheet_name && <span className="font-mono text-[10px] text-zinc-500">{d.sheet_name}</span>}</div>
      {!d.priced && <p className="font-mono text-[11px] text-amber-400">Pending pricing — link a client price sheet so pay can be calculated.</p>}
      <div className="divide-y divide-zinc-800 border border-zinc-800">
        {d.lines.map((l, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
            <div className="flex-1 min-w-0"><div className="text-white truncate">{l.service || "—"}</div><div className="font-mono text-[10px] text-zinc-600">× {l.qty}</div></div>
            {isAdmin && <div className="text-right w-20 shrink-0"><div className="font-mono text-zinc-300">{m(l.client_total)}</div><div className="font-mono text-[9px] text-zinc-600 uppercase">client</div></div>}
            <div className="text-right w-20 shrink-0"><div className="font-mono text-emerald-400">{m(l.contractor_total)}</div><div className="font-mono text-[9px] text-zinc-600 uppercase">pay</div></div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 font-mono text-sm">
        {isAdmin && <span className="text-zinc-400">Client total: <span className="text-white">{m(d.client_total)}</span></span>}
        <span className="text-zinc-400">{isAdmin ? "Contractor total" : "Estimated total"}: <span className="text-emerald-400">{m(d.contractor_total)}</span></span>
      </div>
      <p className="font-mono text-[10px] text-zinc-600">Pay varies per client. Paid within 45 days of completion with all required photos &amp; checklist done.</p>
    </Card>
  );
}

function JobPricingPanel({ job, isAdmin }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/jobs/${job.id}/pricing`).then((r) => setData(r.data)).catch(() => setData(null)); }, [job.id, job.price_sheet_id, job.service_type]);
  if (!data || !data.has_sheet) return null;
  const money = (v) => (v == null ? "By bid" : `$${Number(v).toFixed(2)}`);
  const m = data.matched;
  return (
    <Card className="p-4 space-y-3" data-testid="job-pricing-panel">
      <div className="flex items-center gap-2">
        <DollarSign size={16} className="text-[#FF5722]" />
        <span className="font-head font-bold uppercase text-white text-sm tracking-wide">Pricing</span>
        {data.sheet_name && <span className="font-mono text-[10px] text-zinc-500">{data.sheet_name}</span>}
      </div>
      {m ? (
        <div className="flex flex-wrap gap-3">
          <div className="border border-zinc-800 px-3 py-2" data-testid="pricing-matched">
            <div className="font-mono text-[9px] text-zinc-500 uppercase">{m.service}</div>
            <div className="flex gap-4 mt-1">
              {isAdmin && <div><div className="font-mono text-white">{money(m.client_price)}</div><div className="font-mono text-[9px] text-zinc-600 uppercase">Client / Allowable</div></div>}
              <div><div className="font-mono text-emerald-400" data-testid="pricing-pay">{money(m.contractor_pay)}</div><div className="font-mono text-[9px] text-zinc-600 uppercase">{isAdmin ? "Contractor Pay" : "Your Pay"}</div></div>
            </div>
          </div>
        </div>
      ) : (
        <p className="font-mono text-xs text-zinc-500">No sheet line matches "{data.service_type}". {isAdmin ? "Pick a matching service type or edit the sheet." : ""}</p>
      )}
      {isAdmin && (data.items || []).length > 0 && (
        <details className="group">
          <summary className="font-mono text-[11px] text-zinc-500 cursor-pointer hover:text-[#FF5722]">View all {data.items.length} sheet rates</summary>
          <div className="mt-2 max-h-56 overflow-y-auto divide-y divide-zinc-800 border border-zinc-800">
            {data.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                <span className="flex-1 min-w-0 truncate text-zinc-300">{it.service}</span>
                <span className="font-mono text-white w-20 text-right">{money(it.client_price)}</span>
                <span className="font-mono text-emerald-400 w-20 text-right">{money(it.contractor_pay)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}

function PayoutPanel({ job, setJob, isAdmin, loadHistory }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [allow, setAllow] = useState(job.allowable_payout ?? "");
  const [busy, setBusy] = useState(false);
  const bids = job.payout_bids || [];
  const status = job.payout_status || "none";
  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const STATUS = { none: ["No bid yet", "text-zinc-400"], pending: ["Awaiting admin review", "text-amber-400"], agreed: ["Payout agreed", "text-emerald-400"], counter: ["Counter needed", "text-[#FF5722]"] };
  const [sLabel, sColor] = STATUS[status] || [status, "text-zinc-400"];

  const submitBid = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/payout-bids`, { amount: amt, note });
      setJob(data); setAmount(""); setNote(""); toast.success("Bid sent to admin"); loadHistory?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const decide = async (bidId, decision) => {
    const b = bids.find((x) => x.id === bidId);
    if (decision === "approve" && b?.exceeds_allowable &&
        !window.confirm(`This bid (${fmt(b.amount)}) exceeds the allowable payout${job.allowable_payout != null ? ` of ${fmt(job.allowable_payout)}` : ""}. Approving will cut into your margin. Approve anyway?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/payout-bids/${bidId}/decision`, { decision, note: "" });
      setJob(data); toast.success(decision === "approve" ? "Bid approved" : "Bid declined — contractor can counter"); loadHistory?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveAllow = async () => {
    const v = allow === "" ? null : parseFloat(allow);
    if (allow !== "" && (isNaN(v) || v < 0)) { toast.error("Enter a valid amount"); return; }
    try { const { data } = await api.put(`/jobs/${job.id}`, { allowable_payout: v == null ? undefined : v }); setJob(data); toast.success("Allowable saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="payout-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><DollarSign size={15} className="text-[#FF5722]" /> Job Pricing — Contractor Bid</div>
        <span className={`font-mono text-[11px] uppercase tracking-wide ${sColor}`} data-testid="payout-status">{sLabel}{status === "agreed" && job.contractor_payout != null ? ` · ${fmt(job.contractor_payout)}` : ""}</span>
      </div>
      <p className="text-zinc-500 text-xs -mt-1">Pricing &amp; terms are discussed here between the contractor and admin only.</p>

      {isAdmin ? (
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label>Allowable contractor payout (margin cap)</Label><Input data-testid="allowable-input" type="number" step="0.01" value={allow} onChange={(e) => setAllow(e.target.value)} placeholder="e.g. 250.00" /></div>
          <Btn variant="secondary" data-testid="save-allowable-btn" onClick={saveAllow}><Save size={14} /> Save</Btn>
        </div>
      ) : (
        job.contractor_pay != null ? <div className="font-mono text-xs text-zinc-300 border border-zinc-800 px-3 py-2">Offered payout for this job: <span className="text-emerald-400">{fmt(job.contractor_pay)}</span></div>
        : job.allowable_payout != null && <div className="font-mono text-xs text-zinc-400 border border-zinc-800 px-3 py-2">Allowable up to <span className="text-[#FF5722]">{fmt(job.allowable_payout)}</span> — bids at or below this are approved fastest.</div>
      )}

      {bids.length > 0 && (
        <div className="space-y-2">
          {bids.map((b) => (
            <div key={b.id} data-testid={`payout-bid-${b.id}`} className="border-2 border-zinc-800 p-3 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-head font-bold text-white">{fmt(b.amount)} {b.exceeds_allowable && <span className="font-mono text-[9px] uppercase bg-[#FF5722] text-black border border-black px-1.5 py-0.5 ml-1">exceeds allowable</span>}</span>
                <span className={`font-mono text-[10px] uppercase tracking-wide ${b.status === "approved" ? "text-emerald-400" : b.status === "declined" ? "text-red-400" : b.status === "pending" ? "text-amber-400" : "text-zinc-500"}`}>{b.status}</span>
              </div>
              {b.note && <p className="text-zinc-300 text-sm">"{b.note}"</p>}
              <div className="font-mono text-[10px] text-zinc-600">{b.crew_name} · {new Date(b.created_at).toLocaleString()}</div>
              {b.admin_note && <p className="text-zinc-400 text-xs border-l-2 border-[#FF5722] pl-2">Admin: {b.admin_note}</p>}
              {isAdmin && b.status === "pending" && (
                <div className="flex gap-2 pt-1">
                  <Btn data-testid={`approve-bid-${b.id}`} onClick={() => decide(b.id, "approve")} disabled={busy}><Check size={14} /> Approve</Btn>
                  <Btn variant="danger" data-testid={`decline-bid-${b.id}`} onClick={() => decide(b.id, "decline")} disabled={busy}><X size={14} /> Decline</Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!isAdmin && status !== "agreed" && (
        <div className="space-y-2 border-t border-zinc-800 pt-3">
          <Label>Your bid / counter-offer (USD)</Label>
          <Input data-testid="payout-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 225.00" />
          <Textarea data-testid="payout-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note to admin about pricing or terms…" />
          <Btn data-testid="submit-payout-bid" onClick={submitBid} disabled={busy}>{busy ? "Sending…" : "Submit to Admin"}</Btn>
        </div>
      )}
      {!isAdmin && status === "agreed" && (
        <div className="font-mono text-sm text-emerald-400 border border-emerald-800 bg-emerald-950/20 px-3 py-2">Payout agreed at {fmt(job.contractor_payout)}. You're all set for this job.</div>
      )}
    </Card>
  );
}

function MatchPanel({ jobId }) {
  const [list, setList] = useState(null);
  const [svcType, setSvcType] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignedId, setAssignedId] = useState(null);
  const [radius, setRadius] = useState(60);
  const [err, setErr] = useState(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try { const { data } = await api.get(`/jobs/${jobId}/match-contractors`, { params: { radius } }); setList(data.contractors); setSvcType(data.service_type); }
    catch (e) { const m = formatApiError(e.response?.data?.detail) || "Couldn't match contractors — try again."; setErr(m); setList(null); toast.error(m); }
    finally { setBusy(false); }
  };
  const assign = async (c) => {
    try { await api.post(`/jobs/${jobId}/assign`, { crew_id: c.id }); setAssignedId(c.id); toast.success(`Assigned to ${c.name}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Card className="p-4" data-testid="match-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Label>Match Contractors</Label>
          <div className="font-mono text-xs text-zinc-500">Available contractors within {radius} mi{svcType ? ` · scope: ${svcType}` : ""}. Those offering this service are shown first.</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select data-testid="match-radius" value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="w-auto">
            {[20, 30, 40, 50, 60, 70, 80, 90, 100].map((r) => <option key={r} value={r}>{r} mi</option>)}
          </Select>
          <Btn data-testid="match-contractors-btn" onClick={run} disabled={busy}>{busy ? "Searching…" : "Find"}</Btn>
        </div>
      </div>
      {err && <div data-testid="match-error" className="mt-3 text-red-400 font-mono text-xs border border-red-900 bg-red-950/30 p-2">{err}</div>}
      {list && (list.length === 0
        ? <div className="mt-3 text-zinc-500 font-mono text-sm">No available contractors within {radius} miles.</div>
        : <div className="mt-3 space-y-2">{list.map((c) => (
            <div key={c.id} data-testid={`match-${c.id}`} className={`flex items-center justify-between gap-2 border p-2 ${c.offers_service ? "border-emerald-700 bg-emerald-950/20" : "border-zinc-800 opacity-80"}`}>
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm truncate">{c.name}{c.company ? <span className="text-zinc-500 font-normal"> · {c.company}</span> : ""}</div>
                <div className="font-mono text-[11px] text-zinc-500 truncate">{c.email}{c.service_counties?.length ? ` · ${c.service_counties.join(", ")}` : ""}</div>
                {svcType && (c.offers_service
                  ? <span className="inline-block mt-1 font-mono text-[9px] uppercase bg-emerald-500 text-black border border-black px-1.5 py-0.5">Offers {svcType}</span>
                  : <span className="inline-block mt-1 font-mono text-[9px] uppercase text-zinc-500 border border-zinc-700 px-1.5 py-0.5">Doesn't list this service</span>)}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-xs text-black bg-[#FF5722] border border-black px-2 py-0.5">{c.distance_mi} mi</span>
                <Btn variant="secondary" data-testid={`assign-${c.id}`} onClick={() => assign(c)} disabled={assignedId === c.id}>{assignedId === c.id ? "Assigned" : "Assign"}</Btn>
              </div>
            </div>))}</div>)}
    </Card>
  );
}

function JobMiniMap({ job }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const loc = job.last_crew_location;
  useEffect(() => {
    if (!ref.current || job.lat == null || !loc) return;
    if (!mapRef.current) {
      mapRef.current = L.map(ref.current, { scrollWheelZoom: false, attributionControl: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapRef.current);
    }
    const map = mapRef.current;
    map.eachLayer((l) => { if (l instanceof L.Marker || l instanceof L.Polyline) map.removeLayer(l); });
    const jobIcon = L.divIcon({ className: "", iconSize: [18, 18], iconAnchor: [9, 9], html: `<div style="background:#FF5722;width:16px;height:16px;border:3px solid #000;box-shadow:2px 2px 0 #000;"></div>` });
    const crewIcon = L.divIcon({ className: "", iconSize: [18, 18], iconAnchor: [9, 9], html: `<div style="background:#3B82F6;width:16px;height:16px;border-radius:50%;border:3px solid #000;box-shadow:2px 2px 0 #000;"></div>` });
    L.marker([job.lat, job.lng], { icon: jobIcon }).addTo(map).bindPopup("Job site");
    L.marker([loc.lat, loc.lng], { icon: crewIcon }).addTo(map).bindPopup(loc.by || "Contractor");
    L.polyline([[job.lat, job.lng], [loc.lat, loc.lng]], { color: "#FF5722", weight: 2, dashArray: "5,5" }).addTo(map);
    map.fitBounds([[job.lat, job.lng], [loc.lat, loc.lng]], { padding: [40, 40], maxZoom: 13 });
    setTimeout(() => map.invalidateSize(), 120);
  }, [job, loc]);
  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);
  if (job.lat == null || !loc) return null;
  return (
    <Card className="p-4" data-testid="eta-map">
      <div className="flex items-center justify-between gap-2 mb-2">
        <Label>Contractor Location</Label>
        <span className="font-mono text-xs text-[#FF5722]">{loc.distance_mi != null ? `~${loc.distance_mi} mi · ETA ~${loc.eta_min} min` : "shared"}</span>
      </div>
      <div ref={ref} style={{ height: 220 }} className="border-2 border-zinc-800" />
      <div className="font-mono text-[10px] text-zinc-500 mt-1">Updated {new Date(loc.at).toLocaleString()} by {loc.by}</div>
    </Card>
  );
}

function Detail({ label, value }) {  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</dt>
      <dd className="text-white">{value}</dd>
    </div>
  );
}
